import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../components/public/PublicLayout';
import SEO from '../components/common/SEO';
import { seoConfig } from '../config/seoConfig';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Static surveys that are always shown (hardcoded pages)
const STATIC_SURVEYS = [
    {
        id: 'static-arca2027',
        title: 'ARCA 2027 Conference Survey',
        slug: '__arca2027__',
        description: 'Help shape ARCA\'s first-ever International Conference in Accra, Ghana (October 7-9, 2027). This survey takes approximately 2-3 minutes.',
        end_date: '2026-11-01',
        isStatic: true
    }
];

const SurveysListPage = () => {
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchSurveys();
    }, []);

    const fetchSurveys = async () => {
        try {
            setLoading(true);
            // Fetch published surveys - use the public endpoint by trying common slugs
            // We'll add a public listing endpoint
            const res = await axios.get(`${API_URL}/survey/public-list`);
            if (res.data.success) {
                setSurveys([...STATIC_SURVEYS, ...res.data.data.surveys]);
            } else {
                setSurveys(STATIC_SURVEYS);
            }
        } catch (err) {
            console.error('Failed to load surveys:', err);
            setSurveys(STATIC_SURVEYS);
        } finally { setLoading(false); }
    };

    return (
        <PublicLayout>
            <SEO
                title="Surveys | Africa Respiratory Care Association"
                description="Participate in ARCA surveys and help shape the future of respiratory care in Africa."
                keywords="ARCA surveys, respiratory care survey, healthcare feedback Africa"
                image="/arca.survey.logo.jpg"
                url="/surveys"
            />
            <div className="py-5" style={{ background: 'linear-gradient(135deg, rgba(107,142,35,0.05) 0%, #ffffff 100%)', minHeight: '70vh' }}>
                <div className="container">
                    <div className="row justify-content-center mb-5">
                        <div className="col-lg-8 text-center">
                            <h1 className="h2 fw-bold text-dark mb-3">
                                <i className="bi bi-clipboard-data me-2" style={{ color: '#6B8E23' }}></i>
                                ARCA Surveys
                            </h1>
                            <p className="text-muted">
                                Your voice matters. Participate in our surveys to help shape the future of respiratory care across Africa.
                            </p>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
                    ) : surveys.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="bi bi-clipboard fs-1 text-muted d-block mb-3"></i>
                            <h4 className="text-muted">No active surveys at the moment</h4>
                            <p className="text-muted">Check back soon for new surveys and opportunities to share your feedback.</p>
                            <Link to="/" className="btn btn-outline-primary mt-2">Back to Home</Link>
                        </div>
                    ) : (
                        <div className="row g-4 justify-content-center">
                            {surveys.map(survey => (
                                <div key={survey.id} className="col-md-6 col-lg-5">
                                    <div className="card border-0 shadow-sm h-100" style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}
                                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}>
                                        <div className="card-body p-4">
                                            <div className="d-flex align-items-start mb-3">
                                                <div className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                                                    style={{ width: 48, height: 48, backgroundColor: 'rgba(107,142,35,0.15)' }}>
                                                    <i className="bi bi-clipboard-data" style={{ color: '#6B8E23', fontSize: 20 }}></i>
                                                </div>
                                                <div>
                                                    <h5 className="fw-bold mb-1">{survey.title}</h5>
                                                    {survey.description && <p className="text-muted small mb-0">{survey.description}</p>}
                                                </div>
                                            </div>
                                            {survey.end_date && (
                                                <p className="small mb-3 fw-bold" style={{ color: '#dc3545' }}>
                                                    <i className="bi bi-calendar3 me-1"></i>
                                                    Closes: {new Date(survey.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                                </p>
                                            )}
                                            <Link to={survey.isStatic && survey.slug === '__arca2027__' ? '/conference/arca-2027-survey' : `/survey/${survey.slug}`} className="btn w-100 fw-medium"
                                                style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23', color: 'white' }}>
                                                <i className="bi bi-arrow-right-circle me-2"></i>Take Survey
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </PublicLayout>
    );
};

export default SurveysListPage;
